import { useState } from "react";
import { Company } from "@/lib/company-store";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X, Star, Clock, ExternalLink, Banknote, MapPin, Star as StarIcon } from "lucide-react";

interface CompanyCardProps {
  company: Company;
  index: number;
  onClickOut?: (companyId: string, companyName: string, destinationUrl: string) => void;
}

export function CompanyCard({ company, index, onClickOut }: CompanyCardProps) {
  const [descExpanded, setDescExpanded] = useState(false);
  const features = [
    { label: "Roadside Assistance", value: company.roadsideAssistance },
    { label: "Home Start", value: company.homeStart },
    { label: "National Recovery", value: company.nationalRecovery },
    { label: "Onward Travel", value: company.onwardTravel },
    { label: "European Cover", value: company.europecover },
    { label: "Hire Costs", value: company.hireCosts },
    { label: "Bed & Breakfast", value: company.bedAndBreakfast },
    { label: "Message Relay", value: company.messageRelay },
  ];

  return (
    <div>
      <Card
        className={`relative overflow-hidden transition-shadow duration-200 hover:shadow-lg ${
          company.featured ? "ring-2 ring-primary" : ""
        }`}
        data-testid={`company-card-${company.id}`}
      >
        {company.featured && (
          <div className="absolute top-0 right-0">
            <Badge className="rounded-none rounded-bl-lg bg-orange-700 hover:bg-orange-800 text-white text-sm font-semibold px-4 py-1.5" data-testid={`featured-badge-${company.id}`}>
              Featured
            </Badge>
          </div>
        )}

        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border flex items-center justify-center shrink-0 overflow-hidden">
              {company.logoUrl ? (
                <img 
                  src={company.logoUrl} 
                  alt={`${company.name} logo`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="font-display font-bold text-blue-800 dark:text-blue-300 text-lg">
                  {company.logo}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-lg leading-tight" data-testid={`company-name-${company.id}`}>
                {company.name}
              </h3>
              <p className={`text-sm text-muted-foreground mt-1 ${descExpanded ? "" : "line-clamp-2"}`}>
                {company.description}
              </p>
              {company.description && company.description.length > 80 && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="text-xs text-blue-800 dark:text-blue-300 underline underline-offset-2 hover:no-underline mt-0.5 font-medium"
                  data-testid={`company-desc-toggle-${company.id}`}
                >
                  {descExpanded ? "Show less" : "Read more"}
                </button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 text-[#00b67a]">
                    <StarIcon className="w-4 h-4 fill-[#00b67a]" />
                    <span className="font-bold text-foreground" data-testid={`company-trustpilot-${company.id}`}>{company.trustpilotRating}</span>
                </div>
                <span className="text-[10px] text-muted-foreground">{company.trustpilotReviews} reviews</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span data-testid={`company-response-${company.id}`}>{company.responseTime}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 p-2 rounded border bg-background/50">
              <Banknote className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Excess:</span>
              <span className="font-medium" data-testid={`company-excess-${company.id}`}>
                {company.excess === 0 ? "£0" : `£${company.excess}`}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 rounded border bg-background/50">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium truncate" data-testid={`company-distance-${company.id}`} title={company.coveredDistance}>
                {company.coveredDistance}
              </span>
            </div>
          </div>

          <p className="text-xs font-semibold text-muted-foreground" data-testid={`company-cover-options-${company.id}`}>Cover options Available</p>

          <div className="space-y-2">
            {features.map((feature) => (
              <div
                key={feature.label}
                className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                  feature.value ? "bg-green-50 dark:bg-green-950/30" : "bg-muted/30"
                }`}
              >
                <span className={`text-sm font-medium ${feature.value ? "text-foreground" : "text-muted-foreground"}`}>
                  {feature.label}
                </span>
                {feature.value ? (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-600">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted-foreground/20">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pt-2 border-t">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-sm text-muted-foreground">From</span>
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-bold text-2xl" data-testid={`company-price-${company.id}`}>
                    £{company.pricePerYear}
                  </span>
                  <span className="text-muted-foreground text-sm">/year</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-testid={`company-monthly-price-${company.id}`}>
                  £{(company.pricePerYear / 12).toFixed(2)}/month
                </div>
              </div>
              <Button 
                size="lg" 
                data-testid={`company-cta-${company.id}`} 
                className="bg-green-700 hover:bg-green-800 text-white font-semibold px-6 shadow-md hover:shadow-lg transition-shadow"
                asChild
              >
                <a 
                  href={company.quoteUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label={`Get a quote from ${company.name}`}
                  onClick={() => onClickOut?.(company.id, company.name, company.quoteUrl)}
                >
                  Get Quote
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
